import React from "react";
import { useNavigate } from "react-router-dom";

const style = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #EEF4FA 0%, #F8FAFC 45%, #FFFFFF 100%)",
    fontFamily: "'Manrope', sans-serif",
  },

  navWrapper: {
    maxWidth: "1120px",
    margin: "0 auto",
    padding: "32px 32px 0",
  },

  backButton: {
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#2563EB",
    borderRadius: "999px",
    padding: "8px 14px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },

  contentWrapper: {
    maxWidth: "1120px",
    margin: "0 auto",
    padding: "28px 32px 64px",
  },

  documentCard: {
    background: "#FFFFFF",
    border: "1px solid #D8E1EC",
    borderRadius: "18px",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
    padding: "52px 60px",
  },

  headerBlock: {
    borderBottom: "1px solid #E2E8F0",
    paddingBottom: "28px",
    marginBottom: "32px",
  },

  label: {
    display: "inline-block",
    background: "#DBEAFE",
    color: "#1D4ED8",
    border: "1px solid #BFDBFE",
    borderRadius: "999px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: "18px",
  },

  title: {
    fontSize: "38px",
    color: "#0B1F3A",
    fontWeight: 800,
    letterSpacing: "-0.04em",
    margin: 0,
  },

  subtitle: {
    fontSize: "14px",
    color: "#64748B",
    marginTop: "12px",
    marginBottom: 0,
    fontWeight: 500,
  },

  introText: {
    fontSize: "15px",
    color: "#334155",
    lineHeight: 1.8,
    marginTop: "18px",
    maxWidth: "980px",
  },

  section: {
    marginTop: "34px",
    padding: "24px 26px",
    background: "#F8FAFC",
    border: "1px solid #E2E8F0",
    borderRadius: "14px",
  },

  sectionTitle: {
    fontSize: "20px",
    color: "#0B1F3A",
    fontWeight: 750,
    marginTop: 0,
    marginBottom: "18px",
    paddingBottom: "12px",
    borderBottom: "1px solid #DDE7F1",
  },

  bodyText: {
    fontSize: "14.5px",
    color: "#334155",
    lineHeight: 1.75,
    marginTop: "1rem",
    marginBottom: 0,
  },

  mutedText: {
    fontSize: "14.5px",
    color: "#475569",
    lineHeight: 1.75,
    marginTop: "1rem",
    marginBottom: 0,
    fontStyle: "italic",
  },

  list: {
    display: "block",
    marginTop: "1rem",
    marginBottom: "1rem",
    paddingLeft: "2rem",
    color: "#334155",
    fontSize: "14.5px",
    lineHeight: 1.8,
    listStyleType: "disc",
    listStylePosition: "outside",
  },

  listItem: {
    display: "list-item",
    marginBottom: "0.75rem",
    paddingLeft: "0.25rem",
  },

  strong: {
    color: "#1E293B",
    fontWeight: 750,
  },

  link: {
    color: "#2563EB",
    fontWeight: 700,
    textDecoration: "none",
  },
};

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div style={style.page}>
      <div style={style.navWrapper}>
        <button onClick={() => navigate(-1)} style={style.backButton}>
          ← Back
        </button>
      </div>

      <main style={style.contentWrapper}>
        <article style={style.documentCard}>
          <header style={style.headerBlock}>
            <span style={style.label}>Legal Agreement</span>

            <h1 style={style.title}>DEEBug Terms of Service</h1>

            <p style={style.subtitle}>Last updated June 24, 2026</p>

            <p style={style.introText}>
              This Agreement is between DEEBug and the company or person
              accessing or using the Product. This Agreement consists of: (1)
              the Order Form below and (2) the Framework Terms defined below.
            </p>

            <p style={style.introText}>
              If you are accessing or using the Product on behalf of your
              company, you represent that you are authorized to accept this
              Agreement on behalf of your company. By signing up, accessing, or
              using the Product, User indicates its acceptance of this Agreement
              and agrees to be bound by the terms and conditions of this
              Agreement.
            </p>
          </header>

          <section style={style.section}>
            <h2 style={style.sectionTitle}>Introduction</h2>

            <p style={style.bodyText}>
              <strong style={style.strong}>Framework Terms: </strong>
              This Order Form incorporates and is governed by the Framework
              Terms that are made up of the Key Terms below and the Common Paper{" "}
              <a
                href="https://commonpaper.com/standards/cloud-service-agreement/2.1/"
                style={style.link}
              >
                Cloud Service Agreement Standard Terms Version 2.1
              </a>
              , which are incorporated by reference.
            </p>

            <p style={style.bodyText}>
              <strong style={style.strong}>Cloud Service: </strong>
              DEEBug is a cloud-based software platform that enables Users to
              upload datasets, configure machine learning and deep learning
              experiments, execute supported models, compare model performance,
              visualize results, and receive AI-assisted recommendations
              regarding model selection, debugging, preprocessing, and
              optimization. The Service may integrate third-party machine
              learning frameworks, model repositories, or cloud infrastructure
              providers. Availability of particular models, integrations, or
              computational resources may change from time to time without prior
              notice.
            </p>
          </section>

          <section style={style.section}>
            <h2 style={style.sectionTitle}>Key Terms</h2>

            <p style={style.bodyText}>
              <strong style={style.strong}>User: </strong>
              The company or person who accesses or uses the Product.
            </p>

            <p style={style.bodyText}>
              <strong style={style.strong}>Provider: </strong>
              DEEBug
            </p>

            <p style={style.bodyText}>
              <strong style={style.strong}>Effective Date: </strong>
              The date User first accepts this Agreement.
            </p>

            <p style={style.bodyText}>
              <strong style={style.strong}>Governing Law: </strong>
              These Terms shall be governed by and interpreted in accordance
              with the laws of the Province of Nova Scotia and the federal laws
              of Canada applicable therein. The parties irrevocably submit to the
              exclusive jurisdiction of the courts located in Nova Scotia,
              Canada.
            </p>

            <p style={style.bodyText}>
              <strong style={style.strong}>Chosen Courts: </strong>
              The federal courts of Canada.
            </p>
          </section>

          <section style={style.section}>
            <h2 style={style.sectionTitle}>Acceptable Use</h2>

            <p style={style.bodyText}>
              User agrees to use DEEBug only for lawful purposes and in
              accordance with this Agreement. User is responsible for ensuring
              that all datasets, files, code, prompts, model configurations, and
              other materials uploaded to or processed through the Product
              comply with applicable laws, third-party rights, and institutional
              or organizational policies.
            </p>

            <p style={style.bodyText}>User agrees not to:</p>

            <ul style={style.list}>
              <li style={style.listItem}>
                Upload, process, or share datasets that User does not have the
                legal right, consent, or authorization to use;
              </li>
              <li style={style.listItem}>
                Upload personal, confidential, sensitive, regulated, or
                proprietary information unless User has obtained all required
                permissions and implemented appropriate safeguards;
              </li>
              <li style={style.listItem}>
                Use the Product to violate privacy, intellectual property, data
                protection, academic integrity, employment, or other applicable
                laws;
              </li>
              <li style={style.listItem}>
                Upload malware, malicious code, corrupted files, or content
                designed to disrupt, damage, or interfere with the Product or
                any third-party system;
              </li>
              <li style={style.listItem}>
                Attempt to reverse engineer, decompile, bypass, disable,
                compromise, or interfere with any security, access-control,
                authentication, usage-limit, or monitoring feature of the
                Product;
              </li>
              <li style={style.listItem}>
                Use automated scripts, bots, scraping tools, or other systems to
                overload, abuse, probe, or disrupt the Product or its
                infrastructure;
              </li>
              <li style={style.listItem}>
                Use the Product to develop, test, or deploy unlawful, harmful,
                discriminatory, deceptive, or unsafe machine learning models or
                outputs;
              </li>
              <li style={style.listItem}>
                Misrepresent the source, accuracy, validity, ownership, or
                authorization of any dataset, model, result, or AI-generated
                output;
              </li>
              <li style={style.listItem}>
                Use the Product in a manner that could damage DEEBug, other
                users, third-party service providers, or the integrity,
                availability, or security of the Product; or
              </li>
              <li style={style.listItem}>
                Encourage, assist, or permit any other person to engage in any
                prohibited conduct described in this section.
              </li>
            </ul>

            <p style={style.bodyText}>
              DEEBug reserves the right to suspend, restrict, or terminate
              access to the Product where DEEBug reasonably believes that User
              has violated this Acceptable Use section or that User&apos;s use
              of the Product may create legal, security, operational, or
              reputational risk.
            </p>
          </section>

          <section style={style.section}>
            <h2 style={style.sectionTitle}>Use Limitations</h2>

            <p style={style.bodyText}>
              Limitations on the size of the datasets that users can upload will
              need to be established with client. Fill in more of this section
              later.
            </p>
          </section>

          <section style={style.section}>
            <h2 style={style.sectionTitle}>User Data</h2>

            <p style={style.bodyText}>
              User retains all ownership rights in datasets, code,
              documentation, model configurations, and other materials uploaded
              to the Service (&quot;User Data&quot;).
            </p>

            <p style={style.bodyText}>
              User grants DEEBug a limited, worldwide, non-exclusive license to
              host, process, copy, transmit, cache, and otherwise use User Data
              solely as necessary to provide, maintain, secure, troubleshoot,
              and improve the Service.
            </p>

            <p style={style.bodyText}>
              DEEBug will not claim ownership of User Data.
            </p>
          </section>

          <section style={style.section}>
          <h2 style={style.sectionTitle}>Covered Claims</h2>

          <p style={style.bodyText}>
            To the fullest extent permitted by applicable law, the dispute resolution
            provisions contained in these Terms apply to any dispute, disagreement,
            claim, demand, action, proceeding, or controversy between you and DEEBug
            that arises out of, relates to, or is connected with the Platform, these
            Terms, or the relationship between you and DEEBug. These matters are
            referred to in these Terms as{" "}
            <strong style={style.strong}>“Covered Claims.”</strong>
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Terms and Policies: </strong>
            Covered Claims include disputes relating to the formation, acceptance,
            validity, interpretation, scope, application, enforceability, performance,
            alleged breach, suspension, expiration, or termination of these Terms or
            any related policy, notice, consent, agreement, or supplemental term made
            available through the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Access to and Use of the Platform: </strong>
            Covered Claims include disputes relating to your access to, registration
            for, use of, attempted use of, reliance on, or inability to access or use
            the Platform or any of its features, services, interfaces, integrations,
            documentation, tools, dashboards, workspaces, or related technology.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>User Accounts: </strong>
            Covered Claims include disputes relating to the creation, administration,
            security, verification, restriction, suspension, deactivation, or
            termination of a user account. This includes disputes involving
            unauthorized account access, account credentials, user permissions,
            organizational accounts, or actions taken through an account.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              User Content and Uploaded Materials:{" "}
            </strong>
            Covered Claims include disputes relating to the uploading, importing,
            submitting, storing, hosting, processing, transmitting, modifying,
            displaying, downloading, exporting, sharing, deleting, or loss of
            datasets, source code, scripts, prompts, model files, configuration files,
            project information, documentation, reports, or other materials submitted
            to or generated through the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Datasets and Data Processing: </strong>
            Covered Claims include disputes relating to the source, ownership,
            licensing, legality, integrity, accuracy, completeness, formatting,
            quality, suitability, representativeness, confidentiality, or permitted
            use of any dataset processed through the Platform. This includes claims
            involving corrupted, incomplete, misleading, biased, unlawfully obtained,
            or improperly licensed data.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Machine-Learning Models: </strong>
            Covered Claims include disputes relating to the selection, importation,
            configuration, training, fine-tuning, validation, testing, evaluation,
            execution, comparison, deployment, use, or performance of any
            machine-learning model, algorithm, software package, model component,
            model weight, dependency, or related technology.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Third-Party Models, Datasets, and Services:{" "}
            </strong>
            Covered Claims include disputes relating to any third-party model, dataset,
            repository, library, application programming interface, hosting service,
            authentication provider, cloud-computing provider, payment processor,
            analytics service, or external platform accessed through, linked to,
            integrated with, or used in connection with the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Model Outputs and Results: </strong>
            Covered Claims include disputes relating to any prediction,
            classification, recommendation, score, ranking, benchmark, visualization,
            metric, evaluation, comparison, summary, generated content, analytical
            result, or other output produced, processed, displayed, or made available
            through the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Accuracy and Reliability: </strong>
            Covered Claims include disputes relating to the accuracy, completeness,
            reliability, consistency, reproducibility, timeliness, fairness,
            explainability, suitability, or usefulness of the Platform, a dataset, a
            model, a model comparison, or any output or result.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Bias, Fairness, and Discrimination:{" "}
            </strong>
            Covered Claims include disputes relating to any actual or alleged bias,
            unfairness, discriminatory effect, disparate impact, inappropriate
            classification, or other harmful consequence resulting from a dataset,
            model, model output, user decision, or use of the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Decisions Made Using the Platform:{" "}
            </strong>
            Covered Claims include disputes relating to any decision, action, omission,
            recommendation, conclusion, business process, research activity, academic
            submission, automated process, or other use based in whole or in part on
            information, metrics, comparisons, outputs, or results obtained through
            the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Privacy and Personal Information:{" "}
            </strong>
            Covered Claims include disputes relating to the collection, use,
            disclosure, transfer, storage, retention, processing, protection,
            correction, access, deletion, or other handling of personal information or
            personal data in connection with the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Confidential and Sensitive Information:{" "}
            </strong>
            Covered Claims include disputes relating to any actual or alleged
            disclosure, exposure, misuse, loss, alteration, or unauthorized access
            involving confidential information, proprietary information, trade
            secrets, personal information, sensitive data, credentials, datasets,
            source code, or other protected materials.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Security Incidents: </strong>
            Covered Claims include disputes relating to any actual or alleged security
            vulnerability, cyberattack, unauthorized access, account compromise, data
            breach, malware incident, loss of information, service interruption, or
            failure of security safeguards associated with the Platform or a
            third-party service.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Intellectual Property: </strong>
            Covered Claims include disputes relating to the ownership, licensing, use,
            reproduction, modification, distribution, display, transmission,
            infringement, misappropriation, or alleged violation of copyright,
            trademark, patent, trade-secret, database, moral, publicity, privacy,
            contractual, or other proprietary rights.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Acceptable Use and Enforcement:{" "}
            </strong>
            Covered Claims include disputes relating to any alleged violation of the
            Acceptable Use provisions of these Terms or any investigation, warning,
            restriction, content removal, account suspension, account termination, or
            other enforcement action taken by DEEBug.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Availability and Technical Performance:{" "}
            </strong>
            Covered Claims include disputes relating to any outage, interruption,
            delay, defect, incompatibility, loss of functionality, transmission error,
            processing failure, software error, performance issue, data corruption, or
            inability to access or use any part of the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Changes to the Platform: </strong>
            Covered Claims include disputes relating to any modification, replacement,
            suspension, discontinuation, removal, limitation, or introduction of a
            feature, service, integration, model, dataset, storage option, usage limit,
            pricing arrangement, or other aspect of the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Fees, Subscriptions, and Billing:{" "}
            </strong>
            Where applicable, Covered Claims include disputes relating to any
            subscription, fee, charge, invoice, payment, renewal, cancellation, refund,
            credit, promotion, trial period, tax, pricing change, usage limit, or other
            billing-related matter associated with the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Communications and Representations:{" "}
            </strong>
            Covered Claims include disputes relating to any statement, representation,
            warranty, omission, communication, advertisement, marketing material,
            product description, documentation, support response, demonstration, or
            other information relating to DEEBug or the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Support and Customer Service: </strong>
            Covered Claims include disputes relating to any request for technical
            assistance, customer support, account assistance, complaint resolution,
            data recovery, refund, or other service, including the availability,
            adequacy, timing, or outcome of any response.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Conduct of Other Users: </strong>
            Covered Claims include disputes relating to any content, dataset, model,
            code, communication, activity, omission, misuse, or other conduct by
            another user, organization, collaborator, or third party using or
            interacting with the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Suspension or Termination of Services:{" "}
            </strong>
            Covered Claims include disputes relating to any decision by DEEBug to
            reject registration, limit access, restrict functionality, remove content,
            suspend an account, terminate an account, discontinue a service, or
            prevent a user from accessing the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Alleged Harm or Loss: </strong>
            Covered Claims include disputes relating to any alleged direct, indirect,
            incidental, consequential, special, exemplary, punitive, economic,
            reputational, professional, academic, commercial, privacy-related, or
            other loss, damage, injury, cost, liability, or expense connected with the
            Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Legal and Regulatory Obligations:{" "}
            </strong>
            Covered Claims include allegations that DEEBug, the Platform, a user, a
            dataset, a model, an output, or an activity conducted through the Platform
            violated a law, regulation, regulatory requirement, industry standard,
            professional obligation, contractual duty, or other legal requirement.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Prior, Current, and Future Relationships:{" "}
            </strong>
            Covered Claims include disputes arising from interactions or relationships
            between you and DEEBug that occurred before you accepted these Terms, while
            these Terms are in effect, or after your account or these Terms have been
            suspended or terminated, provided that the dispute is connected with the
            Platform or the relationship between the parties.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Applicable Legal Theories: </strong>
            Covered Claims include claims based on contract, warranty, statute,
            regulation, tort, negligence, gross negligence, misrepresentation, fraud,
            unjust enrichment, restitution, equity, privacy, data protection,
            intellectual property, consumer protection, competition law, or any other
            legal or equitable theory.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Related Parties: </strong>
            Covered Claims also include claims involving DEEBug&apos;s current or
            former owners, operators, developers, officers, directors, employees,
            contractors, representatives, agents, licensors, service providers,
            successors, and permitted assigns, to the extent that the claim relates to
            their involvement with DEEBug or the Platform.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Timing and Form of Claims: </strong>
            A claim may be a Covered Claim regardless of whether it arose before or
            after you accepted these Terms, whether it seeks monetary or non-monetary
            relief, or whether it is brought individually, jointly, collectively,
            representatively, or in another legally permitted capacity.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Rights That Are Not Restricted:{" "}
            </strong>
            Nothing in this section eliminates, restricts, or waives any right, remedy,
            protection, or legal obligation that cannot lawfully be eliminated,
            restricted, or waived under applicable law.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Mandatory Consumer Rights: </strong>
            Nothing in this section prevents a consumer from relying on mandatory
            protections available under the laws of the consumer&apos;s province,
            territory, state, country, or other jurisdiction.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Regulatory Complaints: </strong>
            Nothing in this section prevents any person from submitting a complaint to
            a privacy, data-protection, consumer-protection, competition,
            human-rights, or other governmental or regulatory authority. It also does
            not prevent a regulatory authority or law-enforcement agency from
            investigating or enforcing applicable law.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Urgent Court Relief: </strong>
            Nothing in this section prevents either party from seeking urgent interim
            or injunctive relief from a court of competent jurisdiction where such
            relief is reasonably necessary to prevent immediate or irreparable harm.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Arbitration and Class Proceedings:{" "}
            </strong>
            This section does not, by itself, require arbitration, waive participation
            in a class proceeding, or designate an exclusive court. Any such
            requirement must be expressly stated in another provision of these Terms
            and will apply only to the extent that it is valid and enforceable under
            applicable law.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>International Users: </strong>
            For users located outside Nova Scotia, local laws may provide mandatory
            rights or remedies that apply notwithstanding these Terms. Where a
            provision of these Terms conflicts with a mandatory law that applies to a
            user, that mandatory law will control only to the extent of the conflict.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Effect of Classification: </strong>
            The classification of a matter as a Covered Claim determines the scope of
            the dispute resolution provisions in these Terms. It does not determine
            whether the claim is valid, whether either party is liable, or what
            remedies may ultimately be available.
          </p>
        </section>

        <section style={style.section}>
          <h2 style={style.sectionTitle}>Intellectual Property</h2>

          <p style={style.bodyText}>
            <strong style={style.strong}>DEEBug Proprietary Rights: </strong>
            DEEBug and its licensors retain all right, title, and interest in the
            Service and all related intellectual property. This includes the website,
            application, software, source code, interfaces, designs, graphics, logos,
            trademarks, documentation, workflows, algorithms, model-comparison tools,
            dashboards, and other materials created or provided by DEEBug.
          </p>

          <p style={style.bodyText}>
            DEEBug also owns any updates, improvements, modifications, enhancements,
            and derivative works made to the Service. Except for the limited rights
            expressly granted under these Terms, nothing transfers ownership of
            DEEBug&apos;s intellectual property to you. All rights not expressly
            granted are reserved.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Limited Right to Use the Service: </strong>
            Subject to your compliance with these Terms, DEEBug grants you a limited,
            non-exclusive, non-transferable, non-sublicensable, and revocable right to
            access and use the Service for its intended purposes.
          </p>

          <p style={style.bodyText}>
            You may not copy, reproduce, republish, sell, license, distribute, modify,
            publicly display, create derivative works from, or commercially exploit
            DEEBug&apos;s proprietary materials without prior written permission.
          </p>

          <p style={style.bodyText}>
            You may not remove or alter copyright, trademark, attribution, or other
            proprietary notices displayed through the Service. You may also not use
            DEEBug&apos;s name, trademarks, logos, or branding in a way that suggests
            sponsorship, affiliation, or endorsement without written permission.
          </p>

          <p style={style.bodyText}>
            Unless permitted by applicable law, you may not reverse engineer,
            decompile, disassemble, decode, or attempt to discover the source code,
            underlying structure, algorithms, or non-public functionality of the
            Service.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>User Content Ownership: </strong>
            You retain any ownership rights you hold in the datasets, source code,
            model files, prompts, configurations, documents, and other materials you
            upload or submit through the Service, collectively referred to as{" "}
            <strong style={style.strong}>“User Content.”</strong>
          </p>

          <p style={style.bodyText}>
            You are responsible for ensuring that you own your User Content or have all
            permissions, licences, and legal authority required to upload and process
            it through the Service.
          </p>

          <p style={style.bodyText}>
            You must not upload or use User Content that infringes another
            person&apos;s copyright, trademark, patent, trade secret, privacy,
            confidentiality, contractual, or other proprietary rights.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Licence Required to Provide the Service:{" "}
            </strong>
            You grant DEEBug a limited, non-exclusive, worldwide, and royalty-free
            licence to host, store, reproduce, transmit, display, format, process, and
            otherwise use your User Content only as reasonably necessary to operate,
            maintain, secure, support, and provide the Service.
          </p>

          <p style={style.bodyText}>
            This licence does not transfer ownership of your User Content to DEEBug or
            permit DEEBug to sell your identifiable User Content as its own. The
            licence continues only as long as reasonably necessary to provide the
            Service and meet applicable backup, security, retention, and legal
            obligations.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Models, Datasets, and Third-Party Materials:{" "}
            </strong>
            Models, model weights, datasets, libraries, packages, code, and other
            materials available through the Service may be owned by DEEBug, by you, or
            by third parties.
          </p>

          <p style={style.bodyText}>
            Accessing third-party material through DEEBug does not grant you unlimited
            rights to use it. You are responsible for reviewing and complying with any
            applicable licence terms, attribution requirements, commercial-use
            restrictions, and redistribution conditions.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Model Outputs and Results: </strong>
            Subject to applicable law and third-party licence restrictions, DEEBug
            does not claim ownership of reports, metrics, comparisons, visualizations,
            predictions, or other results generated specifically through your
            authorized use of the Service.
          </p>

          <p style={style.bodyText}>
            However, ownership and copyright protection for machine-generated output
            may vary by jurisdiction. Similar or identical outputs may also be
            generated for different users. DEEBug does not guarantee that an output is
            unique, eligible for copyright protection, or free from third-party
            rights.
          </p>

          <p style={style.bodyText}>
            You are responsible for reviewing outputs before publishing,
            distributing, licensing, commercializing, or relying on them.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Knowledge and Experience: </strong>
            Nothing in these Terms prevents DEEBug from using general knowledge,
            skills, experience, ideas, methods, techniques, and know-how developed
            through the ordinary operation and improvement of the Service, provided
            that DEEBug does not improperly use or disclose your identifiable User
            Content or confidential information.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Feedback: </strong>
            If you voluntarily provide suggestions, ideas, feature requests, error
            reports, or other feedback about the Service, you grant DEEBug a
            perpetual, irrevocable, worldwide, royalty-free, transferable, and
            sublicensable right to use and commercialize that feedback.
          </p>

          <p style={style.bodyText}>
            DEEBug may use feedback to improve the Service or develop new products and
            features without compensation or further permission, provided that doing
            so does not improperly disclose your personal information, confidential
            information, or identifiable User Content.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Aggregated and De-Identified Information:{" "}
            </strong>
            Subject to the Privacy Policy and applicable law, DEEBug may create and use
            aggregated, statistical, or de-identified information about the operation,
            security, performance, and use of the Service.
          </p>

          <p style={style.bodyText}>
            This information may be used to improve the Service, understand usage
            patterns, troubleshoot errors, develop features, and support internal
            analytics, provided that it does not identify you or disclose your
            confidential User Content.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Open-Source Materials: </strong>
            Certain software, models, datasets, documentation, or other materials
            available through or used by the Service may be governed by open-source,
            Creative Commons, open-data, research, or similar third-party licences.
          </p>

          <p style={style.bodyText}>
            These materials remain subject to their applicable licence terms. Nothing
            in these Terms limits rights granted directly to you under an open-source
            licence or grants you rights that exceed those provided by that licence.
          </p>

          <p style={style.bodyText}>
            You are responsible for complying with any applicable attribution,
            disclosure, source-code, redistribution, modification, or similar licence
            requirements.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Copyright Complaints: </strong>
            DEEBug respects the intellectual-property rights of others. If you believe
            that content available through the Service infringes copyright that you
            own or are authorized to enforce, you may submit a written complaint to{" "}
            <a href="mailto:copyright@deebug.ca" style={style.link}>
              copyright@deebug.ca
            </a>
            .
          </p>

          <p style={style.bodyText}>
            Your complaint should include your name and contact information,
            identification of the copyrighted work, the location of the allegedly
            infringing material, an explanation of your rights in the work, a
            statement that you believe the use is unauthorized, and your physical or
            electronic signature.
          </p>

          <p style={style.bodyText}>
            DEEBug may request additional information, contact the affected user,
            restrict or remove disputed material, restore material, preserve relevant
            records, or decline to act where a complaint is incomplete, unsupported,
            misleading, or abusive.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Canadian Copyright Notices:{" "}
            </strong>
            Where applicable, DEEBug may process, retain, or forward notices of claimed
            copyright infringement in accordance with the Canadian Copyright Act and
            other applicable laws.
          </p>

          <p style={style.bodyText}>
            Submission of a complaint does not automatically require DEEBug to remove
            the disputed material and does not constitute a legal determination that
            infringement has occurred.
          </p>

          <p style={style.bodyText}>
            You must not knowingly submit a false, misleading, fraudulent, or abusive
            intellectual-property complaint. DEEBug may restrict or terminate access
            for users who repeatedly infringe intellectual-property rights or submit
            bad-faith complaints.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Supplemental Terms: </strong>
            Certain models, datasets, integrations, enterprise services, repositories,
            or software packages may be subject to additional intellectual-property or
            licence terms.
          </p>

          <p style={style.bodyText}>
            Where supplemental terms apply, they are incorporated into these Terms. If
            they conflict with this section, the supplemental terms will govern the
            applicable feature, material, or service to the extent of the conflict.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Survival: </strong>
            Provisions concerning ownership, User Content, licences, feedback,
            open-source materials, and copyright complaints will continue to apply
            after termination of your account or these Terms where required by their
            nature.
          </p>
        </section>

        <section style={style.section}>
            <h2 style={style.sectionTitle}>AI Output Disclaimer</h2>

            <p style={style.bodyText}>
              <strong style={style.strong}>
                AI-Generated Content Disclaimer:{" "}
              </strong>
              DEEBug may provide artificial intelligence features that generate
              analyses, recommendations, explanations, debugging suggestions,
              code samples, statistical interpretations, natural language
              summaries, documentation, or other informational outputs
              (&quot;AI Output&quot;). AI Output is generated algorithmically
              and may contain inaccuracies, omissions, biases, outdated
              information, or recommendations that are incomplete or unsuitable
              for a User&apos;s particular objectives.
            </p>

            <p style={style.bodyText}>
              User acknowledges and agrees that all AI-generated outputs are
              provided for informational and assistance purposes only. User is
              solely responsible for independently reviewing, testing,
              validating, and verifying any suggestions, code, recommendations,
              or other outputs before relying on or implementing them in any
              environment, including development, testing, production, research,
              or commercial applications.
            </p>

            <p style={style.bodyText}>
              DEEBug makes no representations or warranties regarding the
              accuracy, completeness, reliability, or fitness for a particular
              purpose of any AI-generated output and shall not be liable for any
              losses, damages, errors, security vulnerabilities, or other
              consequences arising from User&apos;s use of or reliance upon such
              outputs.
            </p>
        </section>

        <section style={style.section}>
          <h2 style={style.sectionTitle}>Limitation of Liability</h2>

          <p style={style.bodyText}>
            To the fullest extent permitted by applicable law, DEEBug, its
            affiliates, licensors, employees, contractors, officers,
            directors, and service providers shall not be liable for any
            indirect, incidental, consequential, special, exemplary, or
            punitive damages arising out of or relating to the User&apos;s
            access to or use of the Product, regardless of whether such
            damages are based in contract, tort including negligence, strict
            liability, statute, or any other legal theory, and regardless of
            whether DEEBug has been advised of the possibility of such
            damages.
          </p>

          <p style={style.bodyText}>
            Without limiting the foregoing, DEEBug shall not be liable for any
            loss of profits, revenue, business opportunities, goodwill,
            anticipated savings, datasets, model outputs, research results,
            software, source code, intellectual property, business
            interruption, loss of productivity, or loss or corruption of data
            resulting from the use of, inability to use, or reliance upon the
            Product or any AI-generated output.
          </p>

          <p style={style.bodyText}>
            User acknowledges that the Product is intended to assist with
            machine learning experimentation and analysis and that all model
            configurations, datasets, code, recommendations, AI-generated
            outputs, and performance metrics require independent review and
            validation by the User before implementation in research,
            development, production, or commercial environments. User assumes
            all risks associated with decisions made on the basis of
            information generated by the Product.
          </p>

          <p style={style.bodyText}>
            To the maximum extent permitted by law, DEEBug&apos;s total
            aggregate liability arising out of or relating to this Agreement
            or the use of the Product shall not exceed the greater of (a) the
            total fees paid by User to DEEBug for access to the Product during
            the twelve (12) months immediately preceding the event giving rise
            to the claim, or (b) one hundred Canadian dollars (CAD $100.00)
            where the Product is provided without charge.
          </p>

          <p style={style.bodyText}>
            The limitations and exclusions of liability contained in this
            Agreement shall apply regardless of the number of claims, the form
            of action, or the legal theory asserted, and shall survive the
            termination or expiration of this Agreement to the fullest extent
            permitted under the laws of the Province of Nova Scotia and the
            applicable federal laws of Canada.
          </p>
        </section>

        <section style={style.section}>
          <h2 style={style.sectionTitle}>Termination</h2>

          <p style={style.bodyText}>
            <strong style={style.strong}>Cancellation by You: </strong>
            You may stop using the Service and cancel your account at any time. Before
            cancelling your account, you are responsible for exporting or downloading
            any User Content, datasets, project files, model results, reports, or other
            information that you wish to keep.
          </p>

          <p style={style.bodyText}>
            Account cancellation does not automatically cancel or refund any fees that
            have already been paid or become payable, except where required by
            applicable law or expressly stated in an applicable subscription, order
            form, or supplemental term.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Suspension or Termination by DEEBug:{" "}
            </strong>
            DEEBug may suspend, restrict, or terminate your access to some or all of
            the Service where reasonably necessary to protect the Service, its users,
            DEEBug, or third parties.
          </p>

          <p style={style.bodyText}>
            Reasons for suspension or termination may include a violation of these
            Terms, suspected fraud, unlawful activity, infringement of intellectual
            property rights, misuse of the Service, security risks, non-payment of
            applicable fees, prolonged account inactivity, or conduct that may cause
            harm or legal liability.
          </p>

          <p style={style.bodyText}>
            Where reasonably practicable, DEEBug will provide notice of a suspension
            or termination and may provide an opportunity to correct the issue.
            However, DEEBug may act without prior notice where immediate action is
            reasonably necessary for security, safety, legal compliance, prevention of
            harm, or protection of the Service.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Effect of Termination: </strong>
            When your account is terminated or cancelled, your right to access and use
            the Service ends immediately. You must stop using the Service and any
            DEEBug materials made available only through your account.
          </p>

          <p style={style.bodyText}>
            Termination does not affect any rights, obligations, payment requirements,
            liabilities, or remedies that arose before the termination date.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>
              Deletion of User Content and Account Information:{" "}
            </strong>
            Following account cancellation or termination, DEEBug will use
            commercially reasonable efforts to delete or de-identify your account
            information and User Content stored solely within your account, generally
            within 90 days.
          </p>

          <p style={style.bodyText}>
            You acknowledge that deletion may not be immediate and that some
            information may remain temporarily within backups, recovery systems,
            security logs, audit records, and archived copies until those records are
            deleted or overwritten through DEEBug&apos;s normal retention processes.
          </p>

          <p style={style.bodyText}>
            DEEBug is not required to delete User Content that you shared with another
            user, organization, workspace, or collaborative project where that content
            is also controlled by or available to another authorized account holder.
            Copies independently downloaded, exported, or created by other users are
            outside DEEBug&apos;s control.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Required Retention: </strong>
            Any retained personal information will continue to be handled in
            accordance with the Privacy Policy, the Personal Information Protection
            and Electronic Documents Act (PIPEDA), where applicable, and any other
            applicable privacy laws. Personal information will not be retained longer
            than reasonably necessary for the purpose for which it is retained.
          </p>

          <p style={style.bodyText}>
            Any retained personal information will continue to be handled in
            accordance with the Privacy Policy and applicable privacy laws and will
            not be retained longer than reasonably necessary for the applicable
            purpose.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Restoration of Access: </strong>
            A temporary suspension does not guarantee that access will later be
            restored. DEEBug may require you to correct the issue, verify your
            identity, secure your account, remove prohibited content, or satisfy
            outstanding payment obligations before restoring access.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Discontinuation of the Service: </strong>
            DEEBug may modify, suspend, or discontinue some or all of the Service.
            Where reasonably practicable, DEEBug will provide advance notice of a
            material discontinuation and an opportunity for users to export available
            User Content.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Supplemental Terms: </strong>
            Certain subscriptions, enterprise services, integrations, projects, or
            paid features may have separate service periods, renewal conditions, data
            return procedures, or termination rights described in supplemental terms,
            an order form, a statement of work, or another binding agreement.
          </p>

          <p style={style.bodyText}>
            Where those additional terms apply, they form part of the agreement
            between you and DEEBug. If they conflict with this section, the additional
            terms will govern the applicable service to the extent of the conflict.
          </p>

          <p style={style.bodyText}>
            <strong style={style.strong}>Survival: </strong>
            Provisions that by their nature should continue after termination will
            remain in effect. These may include provisions concerning intellectual
            property, User Content licences, confidentiality, payment obligations,
            disclaimers, limitations of liability, dispute resolution, indemnification,
            and legal compliance.
          </p>
        </section>

          <section style={style.section}>
            <h2 style={style.sectionTitle}>Notice Address</h2>

            <p style={style.bodyText}>
              <strong style={style.strong}>For Provider: </strong>
              <a href="mailto:sigma.jahan@dal.ca" style={style.link}>
                sigma.jahan@dal.ca
              </a>
            </p>

            <p style={style.bodyText}>
              <strong style={style.strong}>For User: </strong>
              The main email address on User&apos;s account.
            </p>
          </section>

          <section style={style.section}>
            <h2 style={style.sectionTitle}>Attachments and Supplements</h2>

            <p style={style.bodyText}>
              Security Policy available at:{" "}
              <a href="https://deebug.dev/security" style={style.link}>
                https://deebug.dev/security
              </a>
            </p>

            <p style={style.bodyText}>
              Privacy Policy available at:{" "}
              <a href="https://deebug.dev/privacy" style={style.link}>
                https://deebug.dev/privacy
              </a>
            </p>
          </section>
        </article>
      </main>
    </div>
  );
};

export default TermsPage;